import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Crown, LogOut, Package, FileText, Plus, MessageCircle, Users, Share2, 
  Copy, Ship, Plane, Calculator, Check, Gift, Star, Zap, Rocket, 
  Clock, Image, Quote, ChevronRight, AlertCircle, AlertTriangle, Search, Loader2, MapPin, Award
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from 'sonner';
import { subscriptionAPI, quoteAPI, productAPI } from '@/lib/api';
import WhatsAppFreightWidget from '@/components/WhatsAppFreightWidget';

// Subscription Plans
const SUBSCRIPTION_PLANS = {
  starter_pack: {
    id: 'starter_pack',
    name: 'Starter Pack',
    price: 5,
    period: 'one-time',
    quotes: 0,  // No quotes for starter pack
    messages: 5,
    pictureExchanges: 2,
    badge: 'BEST VALUE TO START',
    icon: Star,
    color: 'text-green-600',
    hasCalculator: false  // No calculator access
  },
  starter: {
    id: 'starter',
    name: 'Starter',
    price: 7,
    period: 'quarter',
    quotes: 20,
    messages: 50,
    pictureExchanges: 3,
    icon: Zap,
    color: 'text-blue-600',
    hasCalculator: true
  },
  pro: {
    id: 'pro',
    name: 'Pro',
    price: 20,
    period: 'quarter',
    quotes: 100,
    messages: 500,
    pictureExchanges: 10,
    badge: 'Most Popular',
    icon: Rocket,
    color: 'text-purple-600',
    hasCalculator: true
  },
  exporter_plus: {
    id: 'exporter_plus',
    name: 'Exporter Plus',
    price: 50,
    period: 'quarter',
    quotes: 500,
    messages: 'Unlimited',
    pictureExchanges: 50,
    icon: Crown,
    color: 'text-amber-600',
    hasCalculator: true
  }
};

const SellerDashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(JSON.parse(localStorage.getItem('user') || '{}'));
  const [subscription, setSubscription] = useState(null);
  const [products, setProducts] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [referrals, setReferrals] = useState([]);
  const [referralStats, setReferralStats] = useState(null);
  
  // Freight Calculator State
  const [freightType, setFreightType] = useState('sea');
  const [freightForm, setFreightForm] = useState({
    origin: '',
    destination: '',
    weight: '',
    volume: '',
    incoterms: 'FOB',
    cargoDescription: '',
    cargoValue: '',
    hsCode: ''
  });
  
  // HS Code Lookup State
  const [showHSLookup, setShowHSLookup] = useState(false);
  const [hsSearchQuery, setHsSearchQuery] = useState('');
  const [hsSearchResults, setHsSearchResults] = useState([]);
  const [hsSearching, setHsSearching] = useState(false);
  const [hsNeedsClarification, setHsNeedsClarification] = useState(false);
  const [hsClarificationPrompt, setHsClarificationPrompt] = useState('');

  // Find Buyers State
  const [showFindBuyers, setShowFindBuyers] = useState(false);
  const [buyers, setBuyers] = useState([]);
  const [loadingBuyers, setLoadingBuyers] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  // Generate referral code from user ID
  const referralCode = referralStats?.referral_code || (user.id ? `PAC${user.id.substring(0, 8).toUpperCase()}` : 'XXXXXXXX');
  const referralLink = referralStats?.referral_link || `https://pactradex.trade/register?ref=${referralCode}`;

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [subRes, productsRes, quotesRes] = await Promise.all([
        subscriptionAPI.getStatus(),
        productAPI.getProducts({ seller_id: user.id }),
        quoteAPI.getQuotes()
      ]);
      setSubscription(subRes.data);
      setProducts(productsRes.data);
      setQuotes(quotesRes.data);
      
      // Fetch referral stats
      try {
        const refStatsRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/referrals/my-stats`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        if (refStatsRes.ok) {
          const refStats = await refStatsRes.json();
          setReferralStats(refStats);
        }
      } catch (refError) {
        console.log('Referral stats not available');
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
    toast.success('Logged out successfully');
  };

  const copyToClipboard = (text, type) => {
    navigator.clipboard.writeText(text);
    toast.success(`${type} copied to clipboard!`);
  };

  const shareViaWhatsApp = () => {
    const message = `Join ServPac and start exporting! Use my referral link: ${referralLink}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, '_blank');
  };

  // HS Code Lookup Function
  const handleHSLookup = async () => {
    if (!hsSearchQuery.trim()) {
      toast.error('Please enter a product description to look up');
      return;
    }
    
    setHsSearching(true);
    setHsSearchResults([]);
    setHsNeedsClarification(false);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/hs-codes/lookup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ description: hsSearchQuery })
      });
      
      if (!response.ok) {
        throw new Error('Lookup failed');
      }
      
      const data = await response.json();
      
      if (data.success && data.matches?.length > 0) {
        setHsSearchResults(data.matches);
        if (data.needs_clarification) {
          setHsNeedsClarification(true);
          setHsClarificationPrompt(data.clarification_prompt || 'Please select the most appropriate HS code or provide more details.');
        }
      } else {
        setHsNeedsClarification(true);
        setHsClarificationPrompt(data.clarification_prompt || 'No matching HS codes found. Please try a different description.');
      }
    } catch (error) {
      console.error('HS lookup error:', error);
      toast.error('Failed to look up HS code. Please try again.');
    } finally {
      setHsSearching(false);
    }
  };

  const selectHSCode = (hsCode, description) => {
    setFreightForm({ ...freightForm, hsCode: hsCode });
    setShowHSLookup(false);
    setHsSearchQuery('');
    setHsSearchResults([]);
    toast.success(`HS Code ${hsCode} selected`);
  };

  // Fetch Buyers for Exporter Plus users
  const fetchBuyers = async () => {
    setLoadingBuyers(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/buyers`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setBuyers(data.buyers || []);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to load buyers');
      }
    } catch (error) {
      console.error('Failed to fetch buyers:', error);
      toast.error('Failed to load buyers');
    } finally {
      setLoadingBuyers(false);
    }
  };

  // Check if user is a Founding Ambassador - MUST be defined before useEffect
  const isFoundingAmbassador = subscription?.is_founding_ambassador || false;
  
  const currentPlanId = subscription?.plan?.toLowerCase() || subscription?.plan_id || 'starter_pack';
  // Founding Ambassadors always have Exporter Plus access
  const currentPlan = isFoundingAmbassador 
    ? SUBSCRIPTION_PLANS.exporter_plus 
    : (SUBSCRIPTION_PLANS[currentPlanId] || SUBSCRIPTION_PLANS.starter_pack);
  const expiryDate = subscription?.expires_at ? new Date(subscription.expires_at).toLocaleDateString() : '---';

  // Load buyers when modal opens
  useEffect(() => {
    if (showFindBuyers && (currentPlan?.id === 'exporter_plus' || isFoundingAmbassador)) {
      fetchBuyers();
    }
  }, [showFindBuyers, isFoundingAmbassador]);

  const [showQuoteDisclaimer, setShowQuoteDisclaimer] = useState(false);

  const handleFreightEstimate = () => {
    if (!freightForm.origin || !freightForm.destination || !freightForm.weight) {
      toast.error('Please fill in required fields');
      return;
    }
    // Show disclaimer dialog first
    setShowQuoteDisclaimer(true);
  };

  const confirmFreightEstimate = () => {
    setShowQuoteDisclaimer(false);
    // Navigate to quote form with prefilled data
    navigate('/quote/new', { state: { prefill: freightForm, type: freightType === 'sea' ? 'CIF' : 'airfreight' } });
  };

  // Calculate usage based on subscription
  const usage = {
    quotes: { used: quotes.length, limit: subscription?.quotes_limit || 3 },
    messages: { used: messages.length, limit: subscription?.messages_limit || 5 },
    pictureExchanges: { used: 0, limit: subscription?.picture_limit || 2 }
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen">Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-surface">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-14">
            <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/')}>
              <img src="/servpac-logo.png" alt="ServPac" className="w-8 h-8 object-contain" />
              <h1 className="text-xl font-chivo font-bold text-ocean-deep">ServPac</h1>
            </div>
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="sm" onClick={() => navigate('/marketplace')}>
                Marketplace
              </Button>
              <Button data-testid="logout-btn" variant="ghost" size="sm" onClick={handleLogout}>
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-6 max-w-4xl">
        {/* Dashboard Title */}
        <div className="mb-6">
          <h2 className="text-2xl font-chivo font-bold text-slate-900">Seller Dashboard</h2>
          <p className="text-slate-600 text-sm">Manage your subscriptions and connect with buyers</p>
        </div>

        {/* Founding Ambassador Badge - Special Display */}
        {isFoundingAmbassador && (
          <Card className="p-5 mb-6 bg-gradient-to-r from-amber-50 via-yellow-50 to-orange-50 border-2 border-amber-300" data-testid="founding-ambassador-badge">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full flex items-center justify-center shadow-lg">
                <Award className="w-8 h-8 text-white" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h3 className="text-xl font-chivo font-bold text-amber-800">FOUNDING TRADE AMBASSADOR</h3>
                  <span className="px-2 py-1 bg-amber-500 text-white text-xs rounded-full animate-pulse">LIFETIME</span>
                </div>
                <p className="text-amber-700 text-sm mt-1">
                  Thank you for being one of our pioneering ambassadors! You have full access to all Exporter Plus features.
                </p>
                <div className="flex gap-3 mt-2">
                  <span className="text-xs text-amber-600 flex items-center gap-1">
                    <Check className="w-3 h-3" /> Find Buyers
                  </span>
                  <span className="text-xs text-amber-600 flex items-center gap-1">
                    <Check className="w-3 h-3" /> Unlimited Quotes
                  </span>
                  <span className="text-xs text-amber-600 flex items-center gap-1">
                    <Check className="w-3 h-3" /> Priority Support
                  </span>
                </div>
              </div>
            </div>
          </Card>
        )}

        {/* Verification Progress Tracker */}
        <Card className="p-5 mb-6" data-testid="verification-progress-card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-chivo font-semibold text-slate-900">Verification Status</h3>
            <span className={`px-2 py-1 rounded-full text-xs ${
              user.verified_phone && (user.business_type === 'individual' ? user.verified_face : user.verified_business)
                ? 'bg-green-100 text-green-700'
                : 'bg-amber-100 text-amber-700'
            }`}>
              {user.verified_phone && (user.business_type === 'individual' ? user.verified_face : user.verified_business)
                ? 'Fully Verified'
                : 'Incomplete'
              }
            </span>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            {/* Email */}
            <div className={`p-3 rounded-lg border ${user.verified_email ? 'bg-green-50 border-green-200' : 'bg-slate-50 border-slate-200'}`}>
              <div className="flex items-center gap-2 mb-1">
                {user.verified_email ? (
                  <Check className="w-4 h-4 text-green-600" />
                ) : (
                  <Clock className="w-4 h-4 text-slate-400" />
                )}
                <span className="text-sm font-medium text-slate-700">Email</span>
              </div>
              <span className={`text-xs ${user.verified_email ? 'text-green-600' : 'text-slate-400'}`}>
                {user.verified_email ? '✓ Verified' : 'Pending'}
              </span>
            </div>
            
            {/* WhatsApp */}
            <div className={`p-3 rounded-lg border ${user.verified_phone ? 'bg-green-50 border-green-200' : 'bg-slate-50 border-slate-200'}`}>
              <div className="flex items-center gap-2 mb-1">
                {user.verified_phone ? (
                  <Check className="w-4 h-4 text-green-600" />
                ) : (
                  <Clock className="w-4 h-4 text-slate-400" />
                )}
                <span className="text-sm font-medium text-slate-700">WhatsApp</span>
              </div>
              {user.verified_phone ? (
                <span className="text-xs text-green-600">✓ Verified</span>
              ) : (
                <Button 
                  size="sm" 
                  variant="link" 
                  className="h-auto p-0 text-xs text-accent"
                  onClick={() => navigate('/verify/whatsapp')}
                >
                  Verify Now →
                </Button>
              )}
            </div>
            
            {/* Face ID (Individual) or Business (Business) */}
            {user.business_type === 'individual' ? (
              <div className={`p-3 rounded-lg border ${user.verified_face ? 'bg-green-50 border-green-200' : 'bg-slate-50 border-slate-200'}`}>
                <div className="flex items-center gap-2 mb-1">
                  {user.verified_face ? (
                    <Check className="w-4 h-4 text-green-600" />
                  ) : (
                    <Clock className="w-4 h-4 text-slate-400" />
                  )}
                  <span className="text-sm font-medium text-slate-700">Face ID</span>
                </div>
                {user.verified_face ? (
                  <span className="text-xs text-green-600">✓ Verified</span>
                ) : (
                  <Button 
                    size="sm" 
                    variant="link" 
                    className="h-auto p-0 text-xs text-accent"
                    onClick={() => navigate('/verify/face')}
                  >
                    Verify Now →
                  </Button>
                )}
              </div>
            ) : (
              <div className={`p-3 rounded-lg border ${user.verified_business ? 'bg-green-50 border-green-200' : 'bg-slate-50 border-slate-200'}`}>
                <div className="flex items-center gap-2 mb-1">
                  {user.verified_business ? (
                    <Check className="w-4 h-4 text-green-600" />
                  ) : (
                    <Clock className="w-4 h-4 text-slate-400" />
                  )}
                  <span className="text-sm font-medium text-slate-700">Business</span>
                </div>
                {user.verified_business ? (
                  <span className="text-xs text-green-600">✓ Verified</span>
                ) : (
                  <Button 
                    size="sm" 
                    variant="link" 
                    className="h-auto p-0 text-xs text-accent"
                    onClick={() => navigate('/verify/business')}
                  >
                    Verify Now →
                  </Button>
                )}
              </div>
            )}
            
            {/* Subscription Status */}
            <div className={`p-3 rounded-lg border ${subscription ? 'bg-green-50 border-green-200' : 'bg-slate-50 border-slate-200'}`}>
              <div className="flex items-center gap-2 mb-1">
                {subscription ? (
                  <Check className="w-4 h-4 text-green-600" />
                ) : (
                  <Clock className="w-4 h-4 text-slate-400" />
                )}
                <span className="text-sm font-medium text-slate-700">Subscription</span>
              </div>
              <span className={`text-xs ${subscription ? 'text-green-600' : 'text-slate-400'}`}>
                {subscription ? `✓ ${currentPlan?.name || 'Active'}` : 'Not Active'}
              </span>
            </div>
          </div>
          
          {/* Progress bar */}
          <div className="mt-2">
            <div className="flex items-center justify-between text-xs mb-1">
              <span className="text-slate-500">Verification Progress</span>
              <span className="font-medium text-slate-700">
                {[
                  user.verified_email,
                  user.verified_phone,
                  user.business_type === 'individual' ? user.verified_face : user.verified_business,
                  !!subscription
                ].filter(Boolean).length} / 4
              </span>
            </div>
            <div className="w-full bg-slate-200 rounded-full h-2">
              <div 
                className="bg-green-500 h-2 rounded-full transition-all"
                style={{ 
                  width: `${([
                    user.verified_email,
                    user.verified_phone,
                    user.business_type === 'individual' ? user.verified_face : user.verified_business,
                    !!subscription
                  ].filter(Boolean).length / 4) * 100}%` 
                }}
              />
            </div>
          </div>
        </Card>

        {/* Plan Subscription Section */}
        <Card className="p-5 mb-6" data-testid="plan-subscription-card">
          <div className="flex justify-between items-start mb-4">
            <h3 className="text-lg font-chivo font-semibold text-slate-900">Plan Subscription</h3>
            <span className="text-sm text-slate-500">Expires: {expiryDate}</span>
          </div>
          
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div className="text-center p-3 bg-slate-50 rounded-lg">
              <p className="text-xs text-slate-500 mb-1">Quotes</p>
              <p className="text-2xl font-chivo font-bold text-slate-900">
                {usage.quotes.used}<span className="text-base text-slate-400">/{usage.quotes.limit}</span>
              </p>
            </div>
            <div className="text-center p-3 bg-slate-50 rounded-lg">
              <p className="text-xs text-slate-500 mb-1">Messages</p>
              <p className="text-2xl font-chivo font-bold text-slate-900">
                {usage.messages.used}<span className="text-base text-slate-400">/{usage.messages.limit}</span>
              </p>
            </div>
            <div className="text-center p-3 bg-slate-50 rounded-lg">
              <p className="text-xs text-slate-500 mb-1">Picture Exchanges</p>
              <p className="text-2xl font-chivo font-bold text-slate-900">
                {usage.pictureExchanges.used}<span className="text-base text-slate-400">/{usage.pictureExchanges.limit}</span>
              </p>
            </div>
          </div>
          
          <div className="flex justify-between items-center">
            <button className="text-sm text-red-600 hover:underline">Cancel Subscription</button>
            <Button size="sm" variant="outline" onClick={() => navigate('/checkout?package=quick_topup')}>
              Quick Top-up ($5)
            </Button>
          </div>
        </Card>

        {/* Upgrade or Renew - Starter Pack */}
        <Card className="p-5 mb-6 border-2 border-green-200 bg-green-50/30" data-testid="starter-pack-card">
          <div className="flex justify-between items-start">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <Star className="w-6 h-6 text-green-600" />
                <h3 className="text-lg font-chivo font-semibold text-slate-900">Starter Pack</h3>
                <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">Verified Badge</span>
              </div>
              <p className="text-sm text-slate-600 mb-3">Perfect for getting started. Renew when limits are exhausted.</p>
              <div className="flex flex-wrap gap-2 mb-2">
                <span className="px-2 py-1 bg-white border border-slate-200 rounded text-xs">5 Messages</span>
                <span className="px-2 py-1 bg-white border border-slate-200 rounded text-xs">2 Picture exchanges</span>
                <span className="px-2 py-1 bg-white border border-slate-200 rounded text-xs">3 Quotes</span>
              </div>
              <div className="flex gap-2 text-xs text-slate-500">
                <span>Verified Seller Badge</span>
                <span>•</span>
                <span>Renewable anytime</span>
              </div>
            </div>
            <div className="text-right">
              <span className="text-xs text-green-700 font-medium bg-green-100 px-2 py-1 rounded">BEST VALUE TO START</span>
              <p className="text-2xl font-chivo font-bold text-slate-900 mt-2">A$5 <span className="text-sm font-normal text-slate-500">one-time</span></p>
              <Button size="sm" className="mt-2 bg-green-600 hover:bg-green-700" onClick={() => navigate('/checkout?package=starter_pack')}>
                Renew Starter Pack
              </Button>
            </div>
          </div>
        </Card>

        {/* Quarterly Subscriptions */}
        <h3 className="text-lg font-chivo font-semibold text-slate-900 mb-4">Quarterly Subscriptions</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {/* Starter */}
          <Card className="p-4 relative" data-testid="starter-plan-card">
            <Zap className="w-8 h-8 text-blue-600 mb-2" />
            <h4 className="font-chivo font-bold text-lg text-slate-900">Starter</h4>
            <p className="text-2xl font-chivo font-bold text-blue-600">A$7<span className="text-sm font-normal text-slate-500">/quarter</span></p>
            <ul className="mt-3 space-y-1 text-sm text-slate-600">
              <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500" /> 20 Quotes</li>
              <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500" /> 50 Messages</li>
              <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500" /> 3 Picture Exchanges</li>
            </ul>
            <Button className="w-full mt-4" variant="outline" onClick={() => navigate('/checkout?package=starter')}>
              Subscribe Now
            </Button>
          </Card>

          {/* Pro - Most Popular */}
          <Card className="p-4 relative border-2 border-purple-300" data-testid="pro-plan-card">
            <span className="absolute -top-3 left-1/2 transform -translate-x-1/2 px-3 py-1 bg-purple-600 text-white text-xs font-medium rounded-full">
              Most Popular
            </span>
            <Rocket className="w-8 h-8 text-purple-600 mb-2" />
            <h4 className="font-chivo font-bold text-lg text-slate-900">Pro</h4>
            <p className="text-2xl font-chivo font-bold text-purple-600">A$20<span className="text-sm font-normal text-slate-500">/quarter</span></p>
            <ul className="mt-3 space-y-1 text-sm text-slate-600">
              <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500" /> 100 Quotes</li>
              <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500" /> 500 Messages</li>
              <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500" /> 10 Picture Exchanges</li>
            </ul>
            <Button className="w-full mt-4 bg-purple-600 hover:bg-purple-700" onClick={() => navigate('/checkout?package=pro')}>
              Subscribe Now
            </Button>
          </Card>

          {/* Exporter Plus */}
          <Card className="p-4 relative" data-testid="exporter-plus-plan-card">
            <Crown className="w-8 h-8 text-amber-600 mb-2" />
            <h4 className="font-chivo font-bold text-lg text-slate-900">Exporter Plus</h4>
            <p className="text-2xl font-chivo font-bold text-amber-600">A$50<span className="text-sm font-normal text-slate-500">/quarter</span></p>
            <ul className="mt-3 space-y-1 text-sm text-slate-600">
              <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500" /> 500 Quotes</li>
              <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500" /> Unlimited Messages</li>
              <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500" /> 50 Picture Exchanges</li>
            </ul>
            <Button className="w-full mt-4" variant="outline" onClick={() => navigate('/checkout?package=exporter_plus')}>
              Subscribe Now
            </Button>
          </Card>
        </div>

        {/* Quick Stats Cards */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <Card className="p-4" data-testid="product-listings-card">
            <Package className="w-6 h-6 text-slate-700 mb-2" />
            <h4 className="font-medium text-slate-900">Product Listings</h4>
            <p className="text-2xl font-chivo font-bold text-slate-900">{products.length}</p>
            <Button size="sm" variant="outline" className="w-full mt-2" onClick={() => navigate('/marketplace')}>
              Browse Marketplace
            </Button>
          </Card>
          
          <Card className="p-4" data-testid="find-buyers-card">
            <Users className="w-6 h-6 text-slate-700 mb-2" />
            <h4 className="font-medium text-slate-900">Find Buyers</h4>
            {(currentPlan?.id === 'exporter_plus' || isFoundingAmbassador) ? (
              <>
                <p className="text-2xl font-chivo font-bold text-green-600">Active</p>
                <Button size="sm" variant="outline" className="w-full mt-2 border-green-500 text-green-600 hover:bg-green-50" onClick={() => setShowFindBuyers(true)}>
                  Browse Buyers
                </Button>
              </>
            ) : (
              <>
                <p className="text-2xl font-chivo font-bold text-slate-400">Locked</p>
                <Button size="sm" variant="outline" className="w-full mt-2" onClick={() => setShowUpgradeModal(true)}>
                  <Crown className="w-3 h-3 mr-1" /> Exporter Plus Only
                </Button>
              </>
            )}
          </Card>
          
          <Card className="p-4" data-testid="conversations-card">
            <MessageCircle className="w-6 h-6 text-slate-700 mb-2" />
            <h4 className="font-medium text-slate-900">Conversations</h4>
            <p className="text-2xl font-chivo font-bold text-slate-900">{messages.length}</p>
            <Button size="sm" variant="outline" className="w-full mt-2" onClick={() => navigate('/messages')}>
              View Messages
            </Button>
          </Card>
        </div>

        {/* FOUNDING GROWTH PARTNER PROGRAM */}
        <Card className="p-5 mb-6 bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200" data-testid="referral-card">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Crown className="w-6 h-6 text-amber-500" />
              <div>
                <h3 className="text-lg font-chivo font-semibold text-slate-900">Founding Growth Partner Program</h3>
                <p className="text-xs text-amber-600 font-medium">LIMITED TIME - Be one of our first 50 Trade Ambassadors!</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg p-4 mb-4 border border-amber-100">
            <p className="text-sm text-slate-700 mb-2">
              Refer <strong className="text-amber-600">20 qualified sellers</strong> and earn 
              <span className="text-amber-600 font-bold"> 1 YEAR FREE EXPORTER PLUS!</span>
            </p>
            <p className="text-xs text-slate-500">
              Referral qualifies when: Verified + Admin Approved + Active 30 days + Paid subscription
            </p>
          </div>
          
          {/* Progress to Reward */}
          <div className="mb-4">
            <div className="flex justify-between text-sm mb-1">
              <span className="font-medium">Progress to Free Year</span>
              <span className="text-amber-600 font-bold">{referralStats?.qualified_count || 0}/20 qualified</span>
            </div>
            <div className="w-full bg-slate-200 rounded-full h-3">
              <div 
                className="bg-gradient-to-r from-amber-400 to-orange-500 h-3 rounded-full transition-all duration-500"
                style={{ width: `${Math.min(100, ((referralStats?.qualified_count || 0) / 20) * 100)}%` }}
              />
            </div>
          </div>

          {/* Referral Stats */}
          <div className="grid grid-cols-3 gap-3 mb-4">
            <div className="text-center p-3 bg-amber-50 rounded-lg border border-amber-100">
              <Clock className="w-5 h-5 text-amber-500 mx-auto mb-1" />
              <p className="text-xl font-chivo font-bold text-amber-600">{referralStats?.pending_count || 0}</p>
              <p className="text-xs text-slate-500">Pending</p>
            </div>
            <div className="text-center p-3 bg-blue-50 rounded-lg border border-blue-100">
              <Check className="w-5 h-5 text-blue-500 mx-auto mb-1" />
              <p className="text-xl font-chivo font-bold text-blue-600">{referralStats?.verified_count || 0}</p>
              <p className="text-xs text-slate-500">Verified</p>
            </div>
            <div className="text-center p-3 bg-green-50 rounded-lg border border-green-100">
              <Star className="w-5 h-5 text-green-500 mx-auto mb-1" />
              <p className="text-xl font-chivo font-bold text-green-600">{referralStats?.qualified_count || 0}</p>
              <p className="text-xs text-slate-500">Qualified</p>
            </div>
          </div>

          {/* Referral Code */}
          <div className="mb-3">
            <Label className="text-xs text-slate-500">Your Referral Code</Label>
            <div className="flex gap-2">
              <div className="flex-1 p-2 bg-white border rounded font-mono font-bold text-ocean-deep">
                {referralCode}
              </div>
              <Button size="icon" variant="outline" onClick={() => copyToClipboard(referralCode, 'Referral code')}>
                <Copy className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Referral Link */}
          <div className="mb-3">
            <Label className="text-xs text-slate-500">Your Referral Link</Label>
            <div className="flex gap-2">
              <div className="flex-1 p-2 bg-white border rounded text-sm text-slate-600 truncate">
                {referralLink}
              </div>
              <Button size="sm" variant="outline" onClick={() => copyToClipboard(referralLink, 'Referral link')}>
                <Copy className="w-4 h-4 mr-1" /> Copy Link
              </Button>
            </div>
          </div>

          <Button className="w-full bg-[#25D366] hover:bg-[#128C7E]" onClick={shareViaWhatsApp}>
            <Share2 className="w-4 h-4 mr-2" /> Share via WhatsApp
          </Button>

          {/* Recent Referrals */}
          {referrals.length > 0 && (
            <div className="mt-4">
              <p className="text-xs text-slate-500 mb-2">Recent Referrals</p>
              {referrals.map((ref, idx) => (
                <div key={idx} className="flex items-center justify-between p-2 bg-white rounded">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center text-purple-600 font-bold text-sm">
                      {ref.name.charAt(0)}
                    </div>
                    <span className="text-sm font-medium">{ref.name}</span>
                  </div>
                  <span className="text-xs text-green-600 font-medium">{ref.rewards}</span>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Freight Calculator */}
        <Card className="p-5 mb-6" data-testid="freight-calculator-card">
          <div className="flex items-center gap-2 mb-4">
            <Calculator className="w-5 h-5 text-ocean-deep" />
            <div>
              <h3 className="text-lg font-chivo font-semibold text-slate-900">Freight Calculator</h3>
              <p className="text-xs text-slate-500">Get instant shipping cost estimates</p>
            </div>
          </div>

          {/* Check if user has calculator access */}
          {currentPlan?.hasCalculator === false ? (
            <div className="text-center py-8">
              <Calculator className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h4 className="font-semibold text-slate-700 mb-2">Calculator Not Available</h4>
              <p className="text-sm text-slate-500 mb-4">
                Starter Pack does not include freight calculator access.<br />
                Upgrade to Starter, Pro, or Exporter Plus to use this feature.
              </p>
              <Button onClick={() => navigate('/payment/checkout?type=subscription')} variant="outline">
                Upgrade Plan
              </Button>
            </div>
          ) : (
            <>
              {/* Freight Type Toggle */}
              <div className="grid grid-cols-2 gap-2 mb-4">
            <button
              className={`flex items-center justify-center gap-2 p-3 rounded-lg border-2 transition-all ${
                freightType === 'sea' ? 'border-ocean-deep bg-ocean-deep/5' : 'border-slate-200'
              }`}
              onClick={() => setFreightType('sea')}
            >
              <Ship className={`w-5 h-5 ${freightType === 'sea' ? 'text-ocean-deep' : 'text-slate-400'}`} />
              <div className="text-left">
                <p className={`font-medium ${freightType === 'sea' ? 'text-ocean-deep' : 'text-slate-600'}`}>Sea Freight</p>
                <p className="text-xs text-slate-500">20-40 days transit</p>
              </div>
            </button>
            <button
              className={`flex items-center justify-center gap-2 p-3 rounded-lg border-2 transition-all ${
                freightType === 'air' ? 'border-ocean-deep bg-ocean-deep/5' : 'border-slate-200'
              }`}
              onClick={() => setFreightType('air')}
            >
              <Plane className={`w-5 h-5 ${freightType === 'air' ? 'text-ocean-deep' : 'text-slate-400'}`} />
              <div className="text-left">
                <p className={`font-medium ${freightType === 'air' ? 'text-ocean-deep' : 'text-slate-600'}`}>Air Freight</p>
                <p className="text-xs text-slate-500">3-7 days transit</p>
              </div>
            </button>
          </div>

          {/* Form Fields */}
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <Label className="text-sm">Origin Country *</Label>
              <Select value={freightForm.origin} onValueChange={(v) => setFreightForm({...freightForm, origin: v})}>
                <SelectTrigger><SelectValue placeholder="Select origin" /></SelectTrigger>
                <SelectContent>
                  {/* Melanesia */}
                  <SelectItem value="FJ">Fiji</SelectItem>
                  <SelectItem value="PG">Papua New Guinea</SelectItem>
                  <SelectItem value="SB">Solomon Islands</SelectItem>
                  <SelectItem value="VU">Vanuatu</SelectItem>
                  <SelectItem value="NC">New Caledonia</SelectItem>
                  {/* Polynesia */}
                  <SelectItem value="WS">Samoa</SelectItem>
                  <SelectItem value="TO">Tonga</SelectItem>
                  <SelectItem value="PF">French Polynesia (Tahiti)</SelectItem>
                  <SelectItem value="CK">Cook Islands</SelectItem>
                  <SelectItem value="NU">Niue</SelectItem>
                  <SelectItem value="TV">Tuvalu</SelectItem>
                  {/* Micronesia */}
                  <SelectItem value="KI">Kiribati</SelectItem>
                  <SelectItem value="MH">Marshall Islands</SelectItem>
                  <SelectItem value="FM">Micronesia</SelectItem>
                  <SelectItem value="NR">Nauru</SelectItem>
                  <SelectItem value="PW">Palau</SelectItem>
                  <SelectItem value="GU">Guam</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm">Destination Country *</Label>
              <Select value={freightForm.destination} onValueChange={(v) => setFreightForm({...freightForm, destination: v})}>
                <SelectTrigger><SelectValue placeholder="Select destination" /></SelectTrigger>
                <SelectContent>
                  {/* Major Export Partners */}
                  <SelectItem value="AU">Australia</SelectItem>
                  <SelectItem value="NZ">New Zealand</SelectItem>
                  <SelectItem value="US">United States</SelectItem>
                  <SelectItem value="CN">China</SelectItem>
                  <SelectItem value="JP">Japan</SelectItem>
                  {/* Asia Pacific */}
                  <SelectItem value="SG">Singapore</SelectItem>
                  <SelectItem value="HK">Hong Kong</SelectItem>
                  <SelectItem value="KR">South Korea</SelectItem>
                  <SelectItem value="TW">Taiwan</SelectItem>
                  <SelectItem value="MY">Malaysia</SelectItem>
                  <SelectItem value="TH">Thailand</SelectItem>
                  <SelectItem value="ID">Indonesia</SelectItem>
                  <SelectItem value="PH">Philippines</SelectItem>
                  <SelectItem value="IN">India</SelectItem>
                  <SelectItem value="VN">Vietnam</SelectItem>
                  {/* Europe */}
                  <SelectItem value="GB">United Kingdom</SelectItem>
                  <SelectItem value="DE">Germany</SelectItem>
                  <SelectItem value="FR">France</SelectItem>
                  <SelectItem value="NL">Netherlands</SelectItem>
                  <SelectItem value="IT">Italy</SelectItem>
                  <SelectItem value="ES">Spain</SelectItem>
                  <SelectItem value="BE">Belgium</SelectItem>
                  {/* Middle East */}
                  <SelectItem value="AE">United Arab Emirates</SelectItem>
                  <SelectItem value="SA">Saudi Arabia</SelectItem>
                  <SelectItem value="QA">Qatar</SelectItem>
                  {/* Americas */}
                  <SelectItem value="CA">Canada</SelectItem>
                  <SelectItem value="MX">Mexico</SelectItem>
                  <SelectItem value="BR">Brazil</SelectItem>
                  <SelectItem value="CL">Chile</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <Label className="text-sm">Weight (kg) *</Label>
              <Input 
                placeholder="e.g., 500"
                value={freightForm.weight}
                onChange={(e) => setFreightForm({...freightForm, weight: e.target.value})}
              />
            </div>
            <div>
              <Label className="text-sm">Volume (CBM)</Label>
              <Input 
                placeholder="e.g., 2.5"
                value={freightForm.volume}
                onChange={(e) => setFreightForm({...freightForm, volume: e.target.value})}
              />
              <p className="text-xs text-slate-400 mt-1">Optional - for volumetric pricing</p>
            </div>
          </div>

          <div className="mb-4">
            <Label className="text-sm">Incoterms</Label>
            <Select value={freightForm.incoterms} onValueChange={(v) => setFreightForm({...freightForm, incoterms: v})}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="FOB">FOB (Free On Board)</SelectItem>
                <SelectItem value="CIF">CIF (Cost, Insurance, Freight)</SelectItem>
                <SelectItem value="EXW">EXW (Ex Works)</SelectItem>
                <SelectItem value="DDP">DDP (Delivered Duty Paid)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <Label className="text-sm">Cargo Description</Label>
              <Input 
                placeholder="Describe your cargo..."
                value={freightForm.cargoDescription}
                onChange={(e) => setFreightForm({...freightForm, cargoDescription: e.target.value})}
              />
            </div>
            <div>
              <Label className="text-sm">Cargo Value (USD)</Label>
              <Input 
                placeholder="e.g., 5000"
                value={freightForm.cargoValue}
                onChange={(e) => setFreightForm({...freightForm, cargoValue: e.target.value})}
              />
              <p className="text-xs text-slate-400 mt-1">For insurance calculation</p>
            </div>
          </div>

          {/* HS Code Input with Lookup */}
          <div className="mb-4">
            <div className="flex justify-between items-center mb-1">
              <Label className="text-sm">HS Code</Label>
              <button 
                type="button"
                onClick={() => setShowHSLookup(true)}
                className="text-xs text-ocean-deep hover:underline flex items-center gap-1"
                data-testid="hs-code-lookup-btn"
              >
                <Search className="w-3 h-3" /> Look Up
              </button>
            </div>
            <Input 
              placeholder="e.g., 0803.90.00"
              value={freightForm.hsCode}
              onChange={(e) => setFreightForm({...freightForm, hsCode: e.target.value})}
              data-testid="hs-code-input"
            />
            <p className="text-xs text-slate-400 mt-1">
              Pacific Harmonized System code for customs classification
            </p>
          </div>

          {/* Subscription Notice */}
          <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg mb-4">
            <Check className="w-4 h-4 text-green-600" />
            <p className="text-sm text-green-800">
              Freight quotes are included in your <strong>{currentPlan?.name || 'subscription'}</strong>. No additional cost per quote.
            </p>
          </div>

          <Button className="w-full" onClick={handleFreightEstimate}>
            <Calculator className="w-4 h-4 mr-2" /> Get Estimate
          </Button>
            </>
          )}
        </Card>
      </main>

      {/* Quote Disclaimer Dialog */}
      <Dialog open={showQuoteDisclaimer} onOpenChange={setShowQuoteDisclaimer}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Important Notice
            </DialogTitle>
            <DialogDescription className="text-left pt-4">
              <p className="mb-3 text-slate-700">
                <strong>Please note:</strong> The quotation provided is an <strong>estimate only</strong> and may not reflect the final cost.
              </p>
              <p className="mb-3 text-slate-600">
                Several conditions may affect the actual shipping cost, including:
              </p>
              <ul className="list-disc pl-5 mb-3 text-sm text-slate-600 space-y-1">
                <li>Fuel surcharges and seasonal rate adjustments</li>
                <li>Cargo dimensions and actual weight variance</li>
                <li>Port congestion and handling fees</li>
                <li>Customs duties and regulatory requirements</li>
                <li>Insurance and documentation charges</li>
              </ul>
              <p className="text-slate-700 font-medium">
                Final costs should be discussed and confirmed via the email address provided in your quote.
              </p>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setShowQuoteDisclaimer(false)}>
              Cancel
            </Button>
            <Button onClick={confirmFreightEstimate} className="bg-ocean-deep hover:bg-ocean">
              I Understand, Continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* HS Code Lookup Dialog */}
      <Dialog open={showHSLookup} onOpenChange={setShowHSLookup}>
        <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Search className="w-5 h-5 text-ocean-deep" />
              HS Code Lookup
            </DialogTitle>
            <DialogDescription>
              Search for the correct HS code by describing your product
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Search Input */}
            <div>
              <Label className="text-sm font-medium">Product Description</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  placeholder="e.g., dried kava root, coconut oil, vanilla beans, taro..."
                  value={hsSearchQuery}
                  onChange={(e) => setHsSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleHSLookup()}
                  data-testid="hs-search-input"
                />
                <Button onClick={handleHSLookup} disabled={hsSearching}>
                  {hsSearching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                </Button>
              </div>
              <p className="text-xs text-slate-500 mt-1">
                Include details like: material, processing (fresh/dried/processed), form (whole/powder/chips)
              </p>
            </div>

            {/* Clarification Message */}
            {hsNeedsClarification && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5" />
                  <p className="text-sm text-amber-800">{hsClarificationPrompt}</p>
                </div>
              </div>
            )}

            {/* Search Results */}
            {hsSearchResults.length > 0 && (
              <div className="space-y-2">
                <Label className="text-sm font-medium">Matching HS Codes</Label>
                {hsSearchResults.map((result, index) => (
                  <div
                    key={index}
                    className="p-3 bg-slate-50 rounded-lg border border-slate-200 hover:border-ocean-deep hover:bg-ocean-deep/5 cursor-pointer transition-all"
                    onClick={() => selectHSCode(result.hs_code, result.description)}
                    data-testid={`hs-result-${index}`}
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-mono font-bold text-ocean-deep">{result.hs_code}</span>
                          <span className="px-2 py-0.5 bg-slate-200 text-slate-600 text-xs rounded">
                            {result.category}
                          </span>
                        </div>
                        <p className="text-sm text-slate-700 mt-1">{result.description}</p>
                      </div>
                      <div className="text-right">
                        <span className={`text-xs font-medium ${
                          result.confidence >= 80 ? 'text-green-600' : 
                          result.confidence >= 50 ? 'text-amber-600' : 'text-slate-500'
                        }`}>
                          {result.confidence}% match
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Common Pacific Products Quick Select */}
            <div>
              <Label className="text-xs text-slate-500">Quick Select - Common Pacific Exports</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {[
                  { code: '1211.90.11', label: 'Kava (dried)' },
                  { code: '0803.90.00', label: 'Bananas' },
                  { code: '0714.40.00', label: 'Taro' },
                  { code: '1513.11.00', label: 'Coconut Oil' },
                  { code: '0905.10.00', label: 'Vanilla' },
                  { code: '1801.00.00', label: 'Cocoa Beans' },
                  { code: '7101.22.10', label: 'Black Pearls' },
                  { code: '1604.14.00', label: 'Canned Tuna' }
                ].map((item, idx) => (
                  <button
                    key={idx}
                    onClick={() => selectHSCode(item.code, item.label)}
                    className="px-2 py-1 text-xs bg-ocean-deep/10 text-ocean-deep rounded hover:bg-ocean-deep/20 transition-colors"
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowHSLookup(false)}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Find Buyers Modal - Exporter Plus Only */}
      <Dialog open={showFindBuyers} onOpenChange={setShowFindBuyers}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-amber-600" />
              Find Buyers
              <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded-full">Exporter Plus</span>
            </DialogTitle>
            <DialogDescription>
              Browse registered buyers looking for Pacific Island products
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {loadingBuyers ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-ocean-deep" />
                <span className="ml-2 text-slate-500">Loading buyers...</span>
              </div>
            ) : buyers.length > 0 ? (
              <div className="space-y-3">
                {buyers.map((buyer, index) => (
                  <div
                    key={index}
                    className="p-4 bg-slate-50 rounded-lg border border-slate-200 hover:border-ocean-deep hover:bg-ocean-deep/5 transition-all"
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <h4 className="font-semibold text-slate-900">{buyer.full_name}</h4>
                        {buyer.company_name && (
                          <p className="text-sm text-slate-600">{buyer.company_name}</p>
                        )}
                        <div className="flex items-center gap-2 mt-2 text-xs text-slate-500">
                          {buyer.country && (
                            <span className="flex items-center gap-1">
                              <MapPin className="w-3 h-3" /> {buyer.country}
                            </span>
                          )}
                          <span>Joined {new Date(buyer.created_at).toLocaleDateString()}</span>
                        </div>
                      </div>
                      <Button size="sm" variant="outline" onClick={() => {
                        setShowFindBuyers(false);
                        toast.info('Navigate to messages to contact this buyer');
                      }}>
                        <MessageCircle className="w-4 h-4 mr-1" /> Contact
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-400">
                <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No buyers registered yet</p>
                <p className="text-sm">Check back soon for new buyer registrations</p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFindBuyers(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upgrade to Exporter Plus Modal */}
      <Dialog open={showUpgradeModal} onOpenChange={setShowUpgradeModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Crown className="w-6 h-6 text-amber-500" />
              Upgrade to Exporter Plus
            </DialogTitle>
            <DialogDescription className="text-left pt-4">
              <p className="text-slate-700 mb-4">
                <strong>Find Buyers</strong> is an exclusive feature for <span className="text-amber-600 font-semibold">Exporter Plus</span> subscribers.
              </p>
              
              <div className="bg-gradient-to-r from-amber-50 to-orange-50 p-4 rounded-lg border border-amber-200 mb-4">
                <h4 className="font-semibold text-amber-800 mb-2">Exporter Plus Benefits:</h4>
                <ul className="text-sm text-slate-600 space-y-2">
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-600" /> Access to buyer database
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-600" /> 500 freight quotes per quarter
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-600" /> Unlimited messages
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-600" /> 50 picture exchanges
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-600" /> Priority support
                  </li>
                </ul>
              </div>
              
              <p className="text-2xl font-chivo font-bold text-center text-slate-900">
                A$50 <span className="text-base font-normal text-slate-500">/ quarter</span>
              </p>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => setShowUpgradeModal(false)}>
              Maybe Later
            </Button>
            <Button 
              onClick={() => {
                setShowUpgradeModal(false);
                navigate('/checkout?package=exporter_plus');
              }}
              className="bg-amber-500 hover:bg-amber-600"
            >
              <Crown className="w-4 h-4 mr-2" /> Upgrade Now
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* WhatsApp Freight Assistance Widget */}
      <WhatsAppFreightWidget />
    </div>
  );
};

export default SellerDashboard;
